import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  CRMFieldLayoutApiService,
  CRMFieldSchemaApiService,
  CrmObjectInterface,
  FieldGroupInterface as CrmFieldGroupInterface,
  FieldSchema,
  FieldSchemaInterface as CrmFieldSchemaInterface,
  FieldType,
  FieldValueInterface as CrmFieldValueInterface,
  ListFieldGroupsResponseInterface,
  ListFieldSchemaResponseInterface,
  PagedRequestOptionsInterface,
  ValidateCrmObjectResponseValidationErrorInterface,
  ValidateCrmObjectResponseValidationErrorSeverity as ErrorSeverity,
} from '@vendasta/crm';
import {
  EMPTY,
  forkJoin,
  Observable,
  of,
  expand,
  map,
  reduce,
  shareReplay,
  switchMap,
  take,
  combineLatest,
  filter,
} from 'rxjs';
import { CrmObjectService } from './crm-object-service/crm-object.service';
import { TranslateByObjectTypeService } from '../../i18n/translate-by-object-type/translate-by-object-type.service';
import {
  CrmInjectionToken,
  CrmDependencies,
  FieldGroupInterface,
  FieldInterface,
  FieldSchemaInterface,
  FieldValueInterface,
  ObjectType,
} from '../../tokens-and-interfaces';
import { SystemFieldIds } from './field.service';

const MAX_SERVER_PAGE_SIZE = 200;

export interface ValidateErrorsWithFieldSchemas {
  errors: ValidateCrmObjectResponseValidationErrorInterface[];
  fieldSchemaMap: Map<string, FieldSchemaInterface>;
}

@Injectable()
export class CrmFormService {
  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly crmObjectService: CrmObjectService,
    private readonly crmFieldSchemaApiService: CRMFieldSchemaApiService,
    private readonly crmFieldLayoutApiService: CRMFieldLayoutApiService,
    private readonly translationService: TranslateService,
    private readonly translateByObjectTypeService: TranslateByObjectTypeService,
  ) {}

  private handleValidationErrors(objectType: ObjectType, validationResponse: ValidateErrorsWithFieldSchemas): void {
    const invalidSeverityErrors = (validationResponse.errors ?? []).filter(
      (e) => e.severity === ErrorSeverity.VALIDATION_ERROR_SEVERITY_INVALID,
    );
    // only display first error for now
    if (invalidSeverityErrors.length) {
      const firstError = invalidSeverityErrors[0];
      const firstErrorSchema = validationResponse.fieldSchemaMap.get(firstError.details?.uniquenessCheck?.fieldId);
      if (firstError.message) {
        throw new Error(
          this.translateByObjectTypeService.getTranslationByObjectType(objectType, firstError?.message, {
            label: firstErrorSchema?.fieldName?.toLowerCase() || '',
          }),
        );
      }
      throw new Error();
    }
  }

  /*
   * TODO: this will probably start to lag when there's a lot of fields.
   * We should probably change to using tabs for different sets of fields later.
   */
  listAllObjectFields$(objectType: ObjectType): Observable<CrmFieldSchemaInterface[]> {
    return of({
      fieldSchemas: [],
      pagingMetadata: {
        nextCursor: '',
        hasMore: true,
      },
    }).pipe(
      expand((resp: ListFieldSchemaResponseInterface) => {
        if (!resp?.pagingMetadata?.hasMore) {
          return EMPTY;
        }
        return this.listObjectFieldsNextPage$(objectType, {
          cursor: resp.pagingMetadata.nextCursor,
          pageSize: MAX_SERVER_PAGE_SIZE,
        });
      }),
      reduce((acc: CrmFieldSchemaInterface[], val) => [...acc, ...(val?.fieldSchemas || [])], []),
    );
  }

  private listObjectFieldsNextPage$(
    objectType: ObjectType,
    paging: PagedRequestOptionsInterface,
  ): Observable<ListFieldSchemaResponseInterface> {
    // listing for a specific namespace will include standard fields as well
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) =>
        this.crmFieldSchemaApiService.listFieldSchema({
          namespace: namespace,
          crmObjectType: objectType,
          pagingOptions: paging,
        }),
      ),
    );
  }

  /*
   * TODO: this will probably start to lag when there's a lot of fields.
   * We should probably change to using tabs for different sets of fields later.
   */
  listAllObjectFieldGroups$(objectType: ObjectType): Observable<CrmFieldGroupInterface[]> {
    return of({
      fieldSchemas: [],
      pagingMetadata: {
        nextCursor: '',
        hasMore: true,
      },
    }).pipe(
      expand((resp: ListFieldGroupsResponseInterface) => {
        if (!resp?.pagingMetadata?.hasMore) {
          return EMPTY;
        }
        return this.listObjectFieldGroupsNextPage$(objectType, {
          cursor: resp.pagingMetadata.nextCursor,
          pageSize: MAX_SERVER_PAGE_SIZE,
        });
      }),
      reduce((acc: CrmFieldGroupInterface[], val) => [...acc, ...(val?.fieldGroups || [])], []),
    );
  }

  private listObjectFieldGroupsNextPage$(
    objectType: ObjectType,
    paging: PagedRequestOptionsInterface,
  ): Observable<ListFieldGroupsResponseInterface> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) =>
        this.crmFieldLayoutApiService.listFieldGroups({
          namespace: namespace,
          crmObjectType: objectType,
          pagingOptions: paging,
        }),
      ),
    );
  }

  private crmFieldGroupToFormFieldGroup(crmFieldGroup: CrmFieldGroupInterface): FieldGroupInterface {
    return {
      description: crmFieldGroup.description,
      fieldIds: crmFieldGroup.fieldIds || [],
    };
  }

  listAllObjectFormFieldGroups$(objectType: ObjectType): Observable<FieldGroupInterface[]> {
    return this.listAllObjectFieldGroups$(objectType).pipe(
      map((crmFieldGroups) => crmFieldGroups.map(this.crmFieldGroupToFormFieldGroup)),
    );
  }

  private crmFieldSchemasToFormFieldSchemas(crmFieldSchemas: CrmFieldSchemaInterface[]): FieldSchemaInterface[] {
    return crmFieldSchemas.map((crmFieldSchema) => {
      return {
        fieldId: crmFieldSchema.fieldId || '',
        fieldType: crmFieldSchema.fieldType || FieldType.FIELD_TYPE_INVALID,
        fieldName: crmFieldSchema.fieldName || '',
        fieldDescription: crmFieldSchema.fieldDescription || '',
        dropdownOptions: crmFieldSchema.dropdownOptions || [],
        currencyCode: crmFieldSchema.currencyCode || '',
        value: null,
        readOnly: crmFieldSchema.readonly,
      };
    });
  }

  listAllFormFields$(objectType: ObjectType): Observable<FieldSchemaInterface[]> {
    return this.listAllObjectFields$(objectType).pipe(
      map((crmFieldSchemas) => this.crmFieldSchemasToFormFieldSchemas(crmFieldSchemas)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getCrmFieldValueToFormValue(crmFieldValue: CrmFieldValueInterface, fieldType: FieldType): any {
    switch (fieldType) {
      case FieldType.FIELD_TYPE_INTEGER:
      case FieldType.FIELD_TYPE_CURRENCY:
        return crmFieldValue.integerValue;
      case FieldType.FIELD_TYPE_STRING:
      case FieldType.FIELD_TYPE_DROPDOWN:
      case FieldType.FIELD_TYPE_EMAIL:
      case FieldType.FIELD_TYPE_PHONE:
        return crmFieldValue.stringValue;
      case FieldType.FIELD_TYPE_BOOLEAN:
        return crmFieldValue.booleanValue;
      case FieldType.FIELD_TYPE_TAG:
      case FieldType.FIELD_TYPE_STRING_LIST:
        return crmFieldValue.stringValues?.values || [];
      case FieldType.FIELD_TYPE_DATETIME:
      case FieldType.FIELD_TYPE_DATE:
        return crmFieldValue.dateValue;
      case FieldType.FIELD_TYPE_FLOAT:
        return crmFieldValue.floatValue;
      default:
        return null;
    }
  }

  private compareStringList(currentValue: CrmFieldValueInterface, modifiedValue: CrmFieldValueInterface): boolean {
    const currentValues = currentValue.stringValues?.values || [];
    const modifiedValues = modifiedValue.stringValues?.values || [];
    const sortedCurrentValues = currentValues.sort();
    const sortedModifiedValues = modifiedValues.sort();

    if (sortedCurrentValues.length !== sortedModifiedValues.length) {
      return true;
    }
    for (const [index, value] of sortedCurrentValues.entries()) {
      if (value !== sortedModifiedValues[index]) {
        return true;
      }
    }
    return false;
  }

  private isFieldValueEmpty(value: CrmFieldValueInterface, fieldType: FieldType): boolean {
    switch (fieldType) {
      case FieldType.FIELD_TYPE_INTEGER:
        return value.integerValue == null;
      case FieldType.FIELD_TYPE_STRING:
      case FieldType.FIELD_TYPE_DROPDOWN:
      case FieldType.FIELD_TYPE_EMAIL:
      case FieldType.FIELD_TYPE_PHONE:
        return value.stringValue == null;
      case FieldType.FIELD_TYPE_BOOLEAN:
        return value.booleanValue == null;
      case FieldType.FIELD_TYPE_TAG:
      case FieldType.FIELD_TYPE_STRING_LIST:
        return value.stringValues?.values == null;
      case FieldType.FIELD_TYPE_DATETIME:
      case FieldType.FIELD_TYPE_DATE:
        return value.dateValue == null;
      default:
        return true;
    }
  }

  private isModifiedFieldValue(
    currentValue: CrmFieldValueInterface,
    modifiedValue: CrmFieldValueInterface,
    fieldType: FieldType,
  ): boolean {
    switch (fieldType) {
      case FieldType.FIELD_TYPE_INTEGER:
        return currentValue.integerValue !== modifiedValue.integerValue;
      case FieldType.FIELD_TYPE_STRING:
      case FieldType.FIELD_TYPE_DROPDOWN:
      case FieldType.FIELD_TYPE_EMAIL:
      case FieldType.FIELD_TYPE_PHONE:
        return currentValue.stringValue !== modifiedValue.stringValue;
      case FieldType.FIELD_TYPE_BOOLEAN:
        return currentValue.booleanValue !== modifiedValue.booleanValue;
      case FieldType.FIELD_TYPE_TAG:
      case FieldType.FIELD_TYPE_STRING_LIST:
        return this.compareStringList(currentValue, modifiedValue);
      case FieldType.FIELD_TYPE_DATETIME:
      case FieldType.FIELD_TYPE_DATE:
        return currentValue.dateValue.getTime() !== modifiedValue.dateValue.getTime();
      default:
        return false;
    }
  }

  private initializeFormFieldSchemaWithCrmObject(
    fieldSchema: FieldSchemaInterface,
    crmObject: CrmObjectInterface,
  ): void {
    if (!fieldSchema?.fieldId || !crmObject?.fields) {
      return;
    }

    const crmObjectFieldValue = crmObject.fields.find((crmObjectField) => {
      return crmObjectField.fieldId === fieldSchema.fieldId;
    });

    if (!crmObjectFieldValue) {
      return;
    }

    fieldSchema.value = this.getCrmFieldValueToFormValue(crmObjectFieldValue, fieldSchema.fieldType);
  }

  listAllFormFieldsInitializedWithCrmObject$(
    objectType: ObjectType,
    crmObjectId: string,
  ): Observable<FieldSchemaInterface[]> {
    return forkJoin([
      this.crmObjectService.getMultiObject(objectType, [crmObjectId]),
      this.listAllFormFields$(objectType),
    ]).pipe(
      map(([getMultiResponse, fieldSchemas]) => {
        if (getMultiResponse?.crmObjects?.length === 0 || !getMultiResponse?.crmObjects?.[0].crmObjectId) {
          throw new Error(this.translationService.instant('CRM_FORM_SERVICE.RESOURCE_NOT_FOUND_ERROR'));
        } else {
          const crmObject = getMultiResponse.crmObjects[0];
          fieldSchemas.forEach((field) => {
            this.initializeFormFieldSchemaWithCrmObject(field, crmObject);
          });
        }
        return fieldSchemas;
      }),
    );
  }

  validateCrmObject$(
    objectType: ObjectType,
    crmObject: CrmObjectInterface,
  ): Observable<ValidateErrorsWithFieldSchemas> {
    const validationErrors$ = this.crmObjectService
      .validateCrmObject(objectType, crmObject)
      .pipe(map((response) => response.errors || []));
    const fieldSchemaMap$ = this.listAllFormFields$(objectType).pipe(
      map((schemas) => {
        const schemaMap = new Map<string, FieldSchemaInterface>();
        for (const schema of schemas) {
          schemaMap.set(schema.fieldId, schema);
        }
        return schemaMap;
      }),
    );
    return forkJoin([validationErrors$, fieldSchemaMap$]).pipe(
      map(([validationErrors, fieldSchemaMap]) => {
        return {
          errors: validationErrors,
          fieldSchemaMap: fieldSchemaMap,
        };
      }),
    );
  }

  validateAndCreateCrmObject$(objectType: ObjectType, fields: FieldInterface[]): Observable<CrmObjectInterface | null> {
    return of(null).pipe(
      map(() => fields.filter((field) => field.hasModification()).map((field) => field.fieldValue())),
      map((fields) =>
        fields.filter((fieldValue) => {
          return fieldValue?.stringValue !== '';
        }),
      ),
      switchMap((fieldValues) => {
        const crmObject: CrmObjectInterface = {
          fields: fieldValues,
        };
        const validationResponse$ = this.validateCrmObject$(objectType, crmObject);
        return forkJoin([of(fieldValues), validationResponse$]);
      }),
      switchMap(([fieldValues, validationResponse]) => {
        this.handleValidationErrors(objectType, validationResponse);
        if (!fieldValues?.length) {
          return of(null);
        }
        const crmObject: CrmObjectInterface = {};
        fieldValues = this.setSystemFields(crmObject, fieldValues);
        crmObject.fields = fieldValues;

        return this.crmObjectService.createCrmObject(objectType, crmObject).pipe(
          map((resp) => {
            crmObject.crmObjectId = resp.crmObjectId;
            return crmObject;
          }),
        );
      }),
    );
  }

  private setSystemFields(crmObject: CrmObjectInterface, fieldValues: FieldValueInterface[]): FieldValueInterface[] {
    const contactOwnerID = fieldValues.find((field) => field.fieldId === SystemFieldIds.ContactOwnerID);
    if (contactOwnerID) {
      crmObject.ownerId = contactOwnerID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.ContactOwnerID);
    }
    const contactGroupID = fieldValues.find((field) => field.fieldId === SystemFieldIds.ContactGroupID);
    if (contactGroupID) {
      crmObject.groupId = contactGroupID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.ContactGroupID);
    }
    const companyOwnerID = fieldValues.find((field) => field.fieldId === SystemFieldIds.CompanyOwnerID);
    if (companyOwnerID) {
      crmObject.ownerId = companyOwnerID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.CompanyOwnerID);
    }
    const companyGroupID = fieldValues.find((field) => field.fieldId === SystemFieldIds.CompanyGroupID);
    if (companyGroupID) {
      crmObject.groupId = companyGroupID.stringValue;
      fieldValues = fieldValues.filter((field) => field.fieldId !== SystemFieldIds.CompanyGroupID);
    }
    return fieldValues;
  }

  validateAndUpdateCrmObject$(
    objectType: ObjectType,
    crmObjectId: string,
    fields: FieldInterface[],
  ): Observable<CrmObjectInterface | null> {
    return this.validateCrmObject$(objectType, {
      crmObjectId: crmObjectId,
      fields: fields.map((field) => field.fieldValue()),
    }).pipe(
      switchMap((validationResponse) => {
        this.handleValidationErrors(objectType, validationResponse);

        const modifiedFieldValues = fields
          .filter((field) => field.hasModification())
          .map((field) => field.fieldValue());
        if (!modifiedFieldValues?.length) {
          return of(null);
        }

        return this.crmObjectService
          .updateCrmObject(objectType, {
            crmObjectId: crmObjectId,
            fields: modifiedFieldValues,
          })
          .pipe(map(() => ({ crmObjectId: crmObjectId, fields: fields.map((field) => field.fieldValue()) })));
      }),
    );
  }

  validateAndUpdateCrmObjectField$(
    objectType: ObjectType,
    crmObjectId: string,
    modifiedValue: FieldValueInterface,
  ): Observable<CrmObjectInterface | null> {
    const fieldSchema$: Observable<FieldSchema> = this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) =>
        this.crmFieldSchemaApiService.getMultiFieldSchema({
          namespace: namespace,
          crmObjectType: objectType,
          fieldId: [modifiedValue.fieldId],
        }),
      ),
      map((response) => !!response?.fieldSchemas && response?.fieldSchemas[0]),
    );
    const currentCrmObject$ = this.crmObjectService.getMultiObject(objectType, [crmObjectId]).pipe(
      take(1),
      map((response) => {
        if (response.crmObjects.length === 0 || !(response?.crmObjects[0] && response.crmObjects[0].crmObjectId)) {
          return null;
        }
        return response.crmObjects[0];
      }),
      filter((crmObject) => !!crmObject),
    );
    const validationValues$ = combineLatest([currentCrmObject$, fieldSchema$]).pipe(
      map(([currentObject, fieldSchema]) => {
        const currentObjectFields = currentObject?.fields || [];
        const currentValueIndex = currentObjectFields.findIndex(
          (fieldValue) => fieldValue?.fieldId === modifiedValue.fieldId,
        );
        if (this.isFieldValueEmpty(modifiedValue, fieldSchema.fieldType)) {
          return [];
        }
        if (currentValueIndex === -1) {
          return [...currentObjectFields, modifiedValue];
        }
        const currentValue = currentObjectFields[currentValueIndex];
        if (!this.isModifiedFieldValue(currentValue, modifiedValue, fieldSchema.fieldType)) {
          return [];
        }

        currentObjectFields[currentValueIndex] = modifiedValue;
        return [...currentObjectFields];
      }),
    );
    const validationResponse$ = validationValues$.pipe(
      take(1),
      switchMap((fieldValues) => {
        if (!fieldValues?.length) {
          return EMPTY;
        }

        return this.validateCrmObject$(objectType, {
          crmObjectId: crmObjectId,
          fields: fieldValues,
        });
      }),
    );

    return combineLatest([validationResponse$, validationValues$]).pipe(
      take(1),
      switchMap(([validationResponse, validationValues]) => {
        if (!validationValues?.length) {
          return of(null);
        }
        this.handleValidationErrors(objectType, validationResponse);

        return this.crmObjectService
          .updateCrmObject(objectType, {
            crmObjectId: crmObjectId,
            fields: [modifiedValue],
          })
          .pipe(map(() => ({ crmObjectId: crmObjectId, fields: [modifiedValue] })));
      }),
    );
  }
}
